import {
    Check,
    ChevronRight,
    PackageCheck,
    ShieldCheck,
    Sparkles,
} from "lucide-react";
import "./PaymentSuccessOverlay.css";

type PaymentSuccessOverlayProps = {
    open: boolean;
    amount: number;
    paymentMethod: string;
    onContinue: () => void;
};

const formatAmount = (
    value: number
) =>
    new Intl.NumberFormat(
        "en-IN",
        {
            maximumFractionDigits: 2,
        }
    ).format(
        Math.max(
            0,
            value
        )
    );

const PaymentSuccessOverlay = ({
    open,
    amount,
    paymentMethod,
    onContinue,
}: PaymentSuccessOverlayProps) => {
    if (!open) {
        return null;
    }

    const normalizedMethod = paymentMethod.toLowerCase();
    const paymentLabel =
        normalizedMethod === "phonepe"
            ? "PhonePe"
            : normalizedMethod === "cashfree"
            ? "Cashfree"
            : normalizedMethod.includes("wallet") || normalizedMethod.includes("salar")
            ? "Salar Pay"
            : "Salar Pay";

    return (
        <div
            className="payment-success-overlay"
            role="dialog"
            aria-modal="true"
            aria-labelledby="payment-success-title"
        >
            <div className="payment-success-glow payment-success-glow-one" />
            <div className="payment-success-glow payment-success-glow-two" />
            <div className="payment-success-grid" />

            <div className="payment-success-sparkles" aria-hidden="true">
                <span className="sparkle sparkle-1" />
                <span className="sparkle sparkle-2" />
                <span className="sparkle sparkle-3" />
                <span className="sparkle sparkle-4" />
                <span className="sparkle sparkle-5" />
                <span className="sparkle sparkle-6" />
                <span className="sparkle sparkle-7" />
                <span className="sparkle sparkle-8" />
            </div>

            <div className="payment-success-shell">
                <div className="payment-success-topbar">
                    <div className="payment-success-brand-mark">
                        <img
                            src="/assets/img/salarlogo.png"
                            alt="Salar"
                            className="payment-success-brand-logo"
                        />
                    </div>

                    <div className="payment-success-verified">
                        <ShieldCheck
                            size={14}
                            strokeWidth={2}
                        />
                        Verified Payment
                    </div>
                </div>

                <div className="payment-success-content">
                    <div className="payment-success-icon-stage">
                        <span className="payment-success-ring payment-success-ring-one" />
                        <span className="payment-success-ring payment-success-ring-two" />

                        <div className="payment-success-check-badge">
                            <Check
                                size={43}
                                strokeWidth={2.8}
                            />
                        </div>

                        <span className="payment-success-star payment-success-star-one">
                            <Sparkles
                                size={16}
                                strokeWidth={2}
                            />
                        </span>

                        <span className="payment-success-star payment-success-star-two">
                            <Sparkles
                                size={12}
                                strokeWidth={2}
                            />
                        </span>
                    </div>

                    <span className="payment-success-eyebrow">
                        PAYMENT COMPLETED
                    </span>

                    <h1 id="payment-success-title">
                        Payment Successful
                    </h1>

                    <p className="payment-success-message">
                        Your payment was completed securely.
                        Your Salar order is confirmed and ready for processing.
                    </p>

                    <div className="payment-success-amount-card">
                        <span>
                            Amount Paid
                        </span>

                        <strong>
                            ₹
                            {formatAmount(
                                amount
                            )}
                        </strong>

                        <small>
                            via {
                                paymentLabel
                            }
                        </small>
                    </div>

                    <div className="payment-success-details">
                        <div className="payment-success-detail-row">
                            <span>
                                Payment Method
                            </span>

                            <strong>
                                {
                                    paymentLabel
                                }
                            </strong>
                        </div>

                        <div className="payment-success-detail-row">
                            <span>
                                Payment Status
                            </span>

                            <strong className="payment-success-status">
                                <span className="payment-success-status-dot" />
                                Successful
                            </strong>
                        </div>

                        <div className="payment-success-detail-row">
                            <span>
                                Order Status
                            </span>

                            <strong>
                                Confirmed
                            </strong>
                        </div>
                    </div>

                    <div className="payment-success-order-banner">
                        <div className="payment-success-order-icon">
                            <PackageCheck
                                size={20}
                                strokeWidth={1.9}
                            />
                        </div>

                        <div>
                            <strong>
                                Your order is confirmed
                            </strong>

                            <span>
                                We’ll keep you updated as your order moves through processing and delivery.
                            </span>
                        </div>
                    </div>

                    <button
                        type="button"
                        className="payment-success-primary"
                        onClick={
                            onContinue
                        }
                    >
                        <span>
                            Continue Shopping
                        </span>

                        <ChevronRight
                            size={17}
                            strokeWidth={2.1}
                        />
                    </button>

                    <div className="payment-success-footer">
                        <ShieldCheck
                            size={13}
                            strokeWidth={2}
                        />

                        <span>
                            Protected by Salar Secure Payments
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PaymentSuccessOverlay;
